/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { ShieldCheck, AlertTriangle, XCircle, PauseCircle, RefreshCw } from 'lucide-react';
import { LegalStatus } from '../../types';

interface StatusBadgeProps {
  status: LegalStatus;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  showIcon = true,
  size = 'md'
}) => {
  let bgClass = '';
  let textClass = '';
  let borderClass = '';
  let label = '';
  let icon = null;

  switch (status) {
    case 'EnVigeur':
      bgClass = 'bg-[#ED_FAF4] bg-emerald-50';
      textClass = 'text-emerald-700 font-semibold';
      borderClass = 'border-emerald-200';
      label = 'En vigueur';
      icon = <ShieldCheck className="w-3.5 h-3.5 shrink-0" />;
      break;
    case 'Modifie':
      bgClass = 'bg-[#FE_F9EC] bg-amber-50';
      textClass = 'text-amber-700 font-semibold';
      borderClass = 'border-amber-200';
      label = 'Modifié';
      icon = <AlertTriangle className="w-3.5 h-3.5 shrink-0" />;
      break;
    case 'Abroge':
      bgClass = 'bg-[#FE_F2F2] bg-rose-50';
      textClass = 'text-rose-700 font-semibold';
      borderClass = 'border-rose-200';
      label = 'Abrogé';
      icon = <XCircle className="w-3.5 h-3.5 shrink-0" />;
      break;
    case 'Suspendu':
      bgClass = 'bg-gray-50';
      textClass = 'text-gray-600 font-semibold';
      borderClass = 'border-gray-200';
      label = 'Suspendu';
      icon = <PauseCircle className="w-3.5 h-3.5 shrink-0" />;
      break;
    case 'Remplace':
      bgClass = 'bg-sky-50';
      textClass = 'text-sky-700 font-semibold';
      borderClass = 'border-sky-200';
      label = 'Remplacé';
      icon = <RefreshCw className="w-3.5 h-3.5 shrink-0" />;
      break;
    default:
      bgClass = 'bg-slate-50';
      textClass = 'text-slate-600';
      borderClass = 'border-slate-200';
      label = status;
  }

  const paddingClass = size === 'sm' ? 'px-2 py-0.5 text-xs' : size === 'lg' ? 'px-3.5 py-1.5 text-sm' : 'px-2.5 py-1 text-xs';
  const iconSpacingClass = size === 'lg' ? 'gap-1.5' : 'gap-1';

  return (
    <span className={`inline-flex items-center rounded-full border ${bgClass} ${textClass} ${borderClass} ${paddingClass} ${iconSpacingClass} tracking-wide transition-all uppercase`}>
      {showIcon && icon}
      <span>{label}</span>
    </span>
  );
};
